import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

function ZenwyxonLogo() {
    return (
        <img
            src="/images/zenwyxnew6.png"
            alt="Zenwyxon Private Limited"
            style={{ height: 80, width: "auto", objectFit: "contain", display: "block" }}
        />
    );
}
import {
    ArrowRight, ArrowDownLeft, ArrowDownRight, Network, Wallet, Gift,
    CheckCircle2, ShieldCheck, Eye, Zap, Monitor, Headphones,
    TrendingUp, Users, Globe, Star, Phone, Mail, MapPin, Clock,
    ChevronLeft, ChevronRight, Package, Droplets, Briefcase, BadgeCheck,
    Menu, X
} from "lucide-react";

function ImgGallery({ images, alt }) {
    const [idx, setIdx] = useState(0);
    if (images.length === 1) return (
        <img src={images[0]} alt={alt} className="w-full h-56 object-cover" />
    );
    return (
        <div className="relative">
            <img src={images[idx]} alt={`${alt} ${idx + 1}`} className="w-full h-56 object-cover" />
            <button onClick={() => setIdx(i => (i - 1 + images.length) % images.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1 transition-colors">
                <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setIdx(i => (i + 1) % images.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1 transition-colors">
                <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {images.map((_, i) => (
                    <button key={i} onClick={() => setIdx(i)}
                        className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/50"}`} />
                ))}
            </div>
        </div>
    );
}

const PRODUCTS = [
    {
        name: "G-Coil Stove (Electric)",
        subtitle: "Hot Smart Electric Coil Stove",
        icon: Zap,
        images: ["/images/G-Coil-1.webp", "/images/G-Coil-2.webp", "/images/G-Coil-3.webp", "/images/G-Coil-4.webp"],
        price: "₹5,000",
        tagline: "2000W | Glass Top | Single Burner | Electric Cooking Heater",
        specs: [
            "Power: 2000W high-efficiency coil",
            "Glass top with cool-touch body",
            "Single burner — compact & portable",
            "4 adjustable heat settings",
            "Built-in overheat protection",
            "Easy-clean durable surface",
            "Compatible with all flat-bottom cookware",
        ],
        color: "from-orange-50 to-red-50",
        badge: "bg-orange-100 text-orange-700",
    },
    {
        name: "Trolley Suitcase",
        subtitle: "Premium Hard-Shell Travel Bag",
        icon: Briefcase,
        images: ["/images/TROLLEY_SUITECASE.jpeg"],
        price: "₹5,000",
        tagline: "Durable · Lightweight · Lockable",
        specs: [
            "Hard-shell ABS material — scratch-resistant",
            "360° multi-directional spinner wheels",
            "TSA-approved combination lock",
            "Telescopic aluminium pull handle",
            "Spacious main compartment with divider",
            "Interior cross-straps & mesh pocket",
            "Lightweight frame — easy to carry",
        ],
        color: "from-blue-50 to-indigo-50",
        badge: "bg-blue-100 text-blue-700",
    },
    {
        name: "Alkaline Water Jug",
        subtitle: "BPA-Free Multi-Stage Filter Jug",
        icon: Droplets,
        images: ["/images/ALKALINE_WATER_JUG.jpeg"],
        price: "₹5,000",
        tagline: "pH 8.5–9.5 | 8-Stage Filtration | 3.5L Capacity",
        specs: [
            "3.5L total capacity (2L filtered)",
            "8-stage filtration system",
            "Raises water pH to 8.5–9.5",
            "100% BPA-free food-grade material",
            "600-litre filter lifespan (~3 months)",
            "Removes chlorine, heavy metals & odour",
            "Ergonomic pour spout with lid lock",
        ],
        color: "from-cyan-50 to-teal-50",
        badge: "bg-cyan-100 text-cyan-700",
    },
];

const SLIDES = [
    {
        headline: "Work Smart Today,",
        highlight: "Earn Freedom Tomorrow",
        sub: "Join Zenwyxon — India's trusted binary network marketing platform. Build your left and right wings, activate your earnings, and watch your income grow every night.",
    },
    {
        headline: "Dream Big, Work Smart,",
        highlight: "Earn Unlimited",
        sub: "A serious compensation engine for modern networkers. Transparent bonuses, an immutable wallet, and a community that grows with you.",
    },
    {
        headline: "Build Your Network,",
        highlight: "Grow Your Income",
        sub: "Refer people to your left or right wing and earn direct bonuses instantly. Let the nightly pairing engine do the rest.",
    },
];

const WHY_ITEMS = [
    { icon: ShieldCheck, title: "Trusted Business Platform", body: "100% transparent operations with real-time ledger visibility and auditable bonus history." },
    { icon: Eye, title: "Transparent System", body: "Every rupee credited or debited is recorded immutably. No hidden deductions — ever." },
    { icon: Zap, title: "Easy to Start", body: "Register, purchase an activation product, and start referring in minutes from any device." },
    { icon: Monitor, title: "Digital Business Tools", body: "Dashboard, tree visualiser, referral links, and pairing history — all at your fingertips." },
    { icon: Headphones, title: "Professional Support", body: "Our team is available Mon–Sat 8 AM to 10 PM to guide you at every step of your journey." },
    { icon: TrendingUp, title: "Fast Growing Community", body: "Thousands of active members are already building their binary networks across India." },
    { icon: Users, title: "Strong Business Network", body: "A proven binary structure that rewards both depth and breadth of your referral tree." },
    { icon: Globe, title: "Global Vision", body: "Built with scalability in mind — Zenwyxon is designed to grow with your ambitions." },
    { icon: Star, title: "Great Earning Potential", body: "Up to ₹5,000/day in pairing bonuses alone, plus ₹600 direct bonus per referral." },
];

export default function Landing() {
    const [slide, setSlide] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const t = setInterval(() => setSlide(s => (s + 1) % SLIDES.length), 5000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const prev = () => setSlide(s => (s - 1 + SLIDES.length) % SLIDES.length);
    const next = () => setSlide(s => (s + 1) % SLIDES.length);

    return (
        <div className="min-h-screen bg-white font-sans">

            {/* ── Top contact bar ──────────────────────────────────────── */}
            <div className="bg-[#0A1628] text-slate-300 text-sm py-3 px-6 md:px-12">
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                        <a href="tel:+917386323388" className="flex items-center gap-2 hover:text-white transition-colors">
                            <Phone className="w-4 h-4" />
                            +91-73863 23388
                        </a>
                        <span className="text-slate-600">·</span>
                        <span>Call to Our Experts</span>
                        <span className="hidden sm:inline text-slate-600 mx-1">|</span>
                        <a href="mailto:zenwyx.support@gmail.com" className="hidden sm:flex items-center gap-2 hover:text-white transition-colors">
                            <Mail className="w-4 h-4" />
                            zenwyx.support@gmail.com
                        </a>
                    </div>
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Mon–Sat &nbsp;8:00 AM – 10:00 PM
                    </div>
                </div>
            </div>

            {/* ── Sticky Nav ───────────────────────────────────────────── */}
            <header className={`sticky top-0 z-30 transition-all duration-300 bg-white ${scrolled ? "shadow-md border-b border-slate-200" : "border-b border-slate-100"}`}>
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                    <Link to="/"><ZenwyxonLogo /></Link>
                    <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
                        <a href="#about" className="hover:text-[#2563EB] transition-colors">About Us</a>
                        <a href="#why" className="hover:text-[#2563EB] transition-colors">Why Us</a>
                        <a href="#earn" className="hover:text-[#2563EB] transition-colors">Earnings</a>
                        <a href="#opportunity" className="hover:text-[#2563EB] transition-colors">Opportunity</a>
                        <a href="#contact" className="hover:text-[#2563EB] transition-colors">Contact</a>
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-[#2563EB] transition-colors">
                            Log in
                        </Link>
                        <Link to="/register" className="px-4 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1A3C5E] text-white text-sm font-semibold transition-colors">
                            Register
                        </Link>
                        <button
                            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
                            onClick={() => setMenuOpen(o => !o)}
                            aria-label="Toggle menu"
                        >
                            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {menuOpen && (
                    <div className="md:hidden border-t border-slate-200 bg-white px-6 py-4 flex flex-col gap-4 text-sm font-medium text-slate-700">
                        <a href="#about" onClick={() => setMenuOpen(false)} className="hover:text-[#1A3C5E] transition-colors py-1">About Us</a>
                        <a href="#why" onClick={() => setMenuOpen(false)} className="hover:text-[#1A3C5E] transition-colors py-1">Why Us</a>
                        <a href="#earn" onClick={() => setMenuOpen(false)} className="hover:text-[#1A3C5E] transition-colors py-1">Earnings</a>
                        <a href="#opportunity" onClick={() => setMenuOpen(false)} className="hover:text-[#1A3C5E] transition-colors py-1">Opportunity</a>
                        <a href="#contact" onClick={() => setMenuOpen(false)} className="hover:text-[#1A3C5E] transition-colors py-1">Contact</a>
                    </div>
                )}
            </header>

            {/* ── Hero Slider ──────────────────────────────────────────── */}
            <section className="relative overflow-hidden min-h-[500px] md:min-h-[600px] flex items-center"
                style={{ background: "#0A1628" }}>
                {/* atmospheric gradient blobs — matches reference banner */}
                <div className="absolute inset-0 pointer-events-none"
                    style={{ background: "radial-gradient(ellipse 70% 80% at 75% 15%, rgba(37,99,235,0.45) 0%, transparent 65%), radial-gradient(ellipse 50% 60% at 10% 80%, rgba(16,185,129,0.18) 0%, transparent 55%)" }} />
                {/* subtle dot-grid overlay */}
                <div className="absolute inset-0 opacity-[0.07]"
                    style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />

                <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 w-full">
                    <div className="grid md:grid-cols-12 gap-10 items-center">
                        {/* Slide content */}
                        <div className="md:col-span-8">
                            <span className="overline text-blue-300 mb-4 block">Binary Network Compensation Platform</span>
                            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tighter transition-all duration-500">
                                {SLIDES[slide].headline}
                                <br />
                                <span className="text-[#f59e0b]">{SLIDES[slide].highlight}</span>
                            </h1>
                            <p className="mt-6 text-lg text-blue-100 max-w-2xl leading-relaxed transition-all duration-500">
                                {SLIDES[slide].sub}
                            </p>
                            <div className="mt-10 flex flex-wrap gap-3">
                                <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#f59e0b] hover:bg-[#d97706] text-[#1A3C5E] font-bold text-sm transition-colors shadow-lg">
                                    Join Now <ArrowRight className="w-4 h-4" />
                                </Link>
                                <a href="#about" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-sm transition-colors backdrop-blur">
                                    Learn More
                                </a>
                            </div>

                            {/* Slide dots */}
                            <div className="mt-10 flex items-center gap-2">
                                {SLIDES.map((_, i) => (
                                    <button key={i} onClick={() => setSlide(i)}
                                        className={`h-2 rounded-full transition-all ${i === slide ? "w-8 bg-[#f59e0b]" : "w-2 bg-white/30 hover:bg-white/50"}`} />
                                ))}
                                <button onClick={prev} className="ml-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                                <button onClick={next} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"><ChevronRight className="w-4 h-4" /></button>
                            </div>
                        </div>

                        {/* Stats card */}
                        <div className="md:col-span-4">
                            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 text-white space-y-5">
                                <div className="overline text-blue-300">Your Earning Potential</div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-white/10 rounded-xl p-4">
                                        <div className="font-display font-black text-3xl text-[#f59e0b]">₹800</div>
                                        <div className="text-xs text-blue-200 mt-1">Direct Bonus</div>
                                    </div>
                                    <div className="bg-white/10 rounded-xl p-4">
                                        <div className="font-display font-black text-3xl text-[#10b981]">₹500</div>
                                        <div className="text-xs text-blue-200 mt-1">Pairing Bonus</div>
                                    </div>
                                    <div className="bg-white/10 rounded-xl p-4">
                                        <div className="font-display font-black text-3xl text-white">100%</div>
                                        <div className="text-xs text-blue-200 mt-1">Net Payout Rate</div>
                                    </div>
                                    <div className="bg-white/10 rounded-xl p-4">
                                        <div className="font-display font-black text-3xl text-[#ff7f50]">10×</div>
                                        <div className="text-xs text-blue-200 mt-1">Max Pairs Per Day</div>
                                    </div>
                                </div>
                                <div className="border-t border-white/20 pt-4">
                                    <div className="text-xs text-blue-300 mb-1">Max daily earnings</div>
                                    <div className="font-display font-black text-2xl">₹5,000 <span className="text-sm font-normal text-blue-200">+ direct bonus per referral</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── About ────────────────────────────────────────────────── */}
            <section id="about" className="py-20 md:py-28 px-6 md:px-12 bg-white">
                <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="overline text-[#2563EB] mb-4">About Zenwyxon</div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#1A3C5E] leading-tight">
                            Empowering Growth.<br />Creating Opportunities.
                        </h2>
                        <p className="mt-6 text-slate-600 text-lg leading-relaxed">
                            Zenwyxon is a next-generation binary network marketing platform built on innovation, technology, and a powerful community. We combine a transparent compensation plan with real-time digital tools so every member can build their business with confidence.
                        </p>
                        <p className="mt-4 text-slate-500 leading-relaxed">
                            Whether you are just starting out or an experienced networker, our platform gives you the structure, support, and earning potential to achieve your financial goals — with complete visibility at every step.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2563EB] hover:bg-[#1A3C5E] text-white font-semibold text-sm transition-colors">
                                Get Started <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a href="#earn" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-200 hover:border-[#2563EB] text-slate-700 hover:text-[#2563EB] font-semibold text-sm transition-colors">
                                View Earnings Plan
                            </a>
                        </div>
                    </div>

                    {/* Visual: binary tree illustration */}
                    <div className="relative">
                        <div className="bg-gradient-to-br from-[#1A3C5E] to-[#2563EB] rounded-2xl p-8 shadow-xl">
                            <div className="overline text-blue-300 mb-6">Binary Tree Preview</div>
                            {/* Root node */}
                            <div className="flex justify-center mb-4">
                                <div className="bg-[#f59e0b] text-[#1A3C5E] font-bold text-xs px-4 py-2 rounded-full shadow-lg">You (Root)</div>
                            </div>
                            {/* Connector */}
                            <div className="flex justify-center mb-2">
                                <div className="w-px h-5 bg-white/30" />
                            </div>
                            {/* Level 1 */}
                            <div className="flex justify-around mb-2">
                                <div className="flex flex-col items-center gap-1.5">
                                    <div className="bg-[#10b981] text-white font-bold text-xs px-3 py-1.5 rounded-full">Left L1</div>
                                    <div className="w-px h-4 bg-white/30" />
                                </div>
                                <div className="flex flex-col items-center gap-1.5">
                                    <div className="bg-[#ff7f50] text-white font-bold text-xs px-3 py-1.5 rounded-full">Right L1</div>
                                    <div className="w-px h-4 bg-white/30" />
                                </div>
                            </div>
                            {/* Level 2 */}
                            <div className="flex justify-around">
                                {["LL", "LR", "RL", "RR"].map((label, i) => (
                                    <div key={label} className={`text-white font-bold text-xs px-2.5 py-1 rounded-full ${i < 2 ? "bg-[#10b981]/70" : "bg-[#ff7f50]/70"}`}>
                                        {label}
                                    </div>
                                ))}
                            </div>
                            {/* Pair counter */}
                            <div className="mt-6 bg-white/10 rounded-xl p-4 border border-white/20">
                                <div className="flex items-center justify-between text-white text-sm">
                                    <div className="flex items-center gap-2">
                                        <ArrowDownLeft className="w-4 h-4 text-[#10b981]" />
                                        <span>Left active: <b>42</b></span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <ArrowDownRight className="w-4 h-4 text-[#ff7f50]" />
                                        <span>Right active: <b>38</b></span>
                                    </div>
                                </div>
                                <div className="mt-2 text-xs text-blue-200">38 matched pairs → ₹14,250 earned</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Why Choose Zenwyxon ─────────────────────────────────── */}
            <section id="why" className="py-20 md:py-28 px-6 md:px-12 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="overline text-[#2563EB] mb-4">Why Choose Us</div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#1A3C5E]">Why Choose Zenwyxon</h2>
                        <p className="mt-4 text-slate-500 text-lg leading-relaxed">Everything you need to build a sustainable network marketing business — in one platform.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {WHY_ITEMS.map((item, i) => (
                            <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-[#2563EB]/30 transition-all group">
                                <div className="w-11 h-11 rounded-xl bg-blue-50 group-hover:bg-[#2563EB] text-[#2563EB] group-hover:text-white grid place-items-center transition-colors mb-4">
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <h3 className="font-display font-bold text-lg text-[#1A3C5E] tracking-tight">{item.title}</h3>
                                <p className="mt-2 text-slate-500 text-sm leading-relaxed">{item.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Mission & Vision ─────────────────────────────────────── */}
            <section className="py-20 md:py-28 px-6 md:px-12 bg-slate-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-xl mx-auto mb-14">
                        <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#2563EB] bg-blue-50 border border-blue-100 rounded-full px-4 py-1.5 mb-5">Our Purpose</span>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-[#0A1628]">Mission &amp; Vision</h2>
                        <p className="mt-4 text-slate-500 leading-relaxed">What drives us every day and where we're headed.</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">

                        {/* Mission */}
                        <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0A1628 0%, #0f2647 60%, #1a3c5e 100%)" }}>
                            {/* amber top accent bar */}
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400" />
                            {/* subtle grid pattern */}
                            <div className="absolute inset-0 opacity-[0.04]"
                                style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
                            <div className="relative p-10">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                                        style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)" }}>
                                        <TrendingUp className="w-7 h-7 text-amber-400" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-widest text-amber-400 mb-0.5">Our Mission</div>
                                        <div className="text-white/30 text-xs">What we do every day</div>
                                    </div>
                                </div>
                                <h3 className="text-white font-bold text-2xl leading-snug mb-5">
                                    Create a platform for sustainable growth and strong business networks
                                </h3>
                                <p className="text-slate-300 leading-relaxed text-sm">
                                    We are committed to building a platform where every member — regardless of experience — can create a sustainable source of income through a fair, transparent, and technology-driven binary compensation system.
                                </p>
                                <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />
                                    <span className="text-slate-400 text-xs">Transparent · Fair · Technology-driven</span>
                                </div>
                            </div>
                        </div>

                        {/* Vision */}
                        <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0A1628 0%, #0f2647 60%, #1a3c5e 100%)" }}>
                            {/* blue top accent bar */}
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-blue-400 to-cyan-400" />
                            <div className="absolute inset-0 opacity-[0.04]"
                                style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
                            <div className="relative p-10">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                                        style={{ background: "rgba(37,99,235,0.18)", border: "1px solid rgba(37,99,235,0.35)" }}>
                                        <Globe className="w-7 h-7 text-blue-400" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-0.5">Our Vision</div>
                                        <div className="text-white/30 text-xs">Where we're headed</div>
                                    </div>
                                </div>
                                <h3 className="text-white font-bold text-2xl leading-snug mb-5">
                                    Become the most trusted binary marketing platform for financial independence
                                </h3>
                                <p className="text-slate-300 leading-relaxed text-sm">
                                    We envision a future where every networker has access to a powerful, honest, and rewarding platform — one that pays on time, every time, with complete transparency and zero surprises.
                                </p>
                                <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" />
                                    <span className="text-slate-400 text-xs">Trusted · Timely · Zero hidden fees</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ── Business Opportunity ─────────────────────────────────── */}
            <section id="opportunity" className="py-20 md:py-28 px-6 md:px-12 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="overline text-[#2563EB] mb-4">Business Opportunity</div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#1A3C5E] leading-tight">
                            A Real Business, Not Just a Dream
                        </h2>
                        <p className="mt-6 text-slate-600 leading-relaxed">
                            Zenwyxon gives you a genuine business framework — a binary tree that you own, bonuses that are calculated transparently, and a wallet that you can withdraw from whenever you qualify.
                        </p>
                        <ul className="mt-8 space-y-4">
                            {[
                                "Flexible — work from anywhere, at your own pace",
                                "Growth-based binary system — every referral counts",
                                "Digital tools — tree view, referral links, earnings dashboard",
                                "Supportive community — learn from active members",
                                "Unlimited earning potential — no income ceiling",
                            ].map((t, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-[#10b981] mt-0.5 shrink-0" />
                                    <span className="text-slate-700">{t}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to="/register" className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#1A3C5E] hover:bg-[#2563EB] text-white font-semibold text-sm transition-colors">
                            Claim Your Spot <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {/* How it works steps */}
                    <div className="space-y-4">
                        {[
                            { icon: Gift, step: "01", title: "Purchase & Activate", body: "Buy an activation product (₹800+). Your account becomes active and your referrer earns a ₹800 direct bonus instantly." },
                            { icon: Network, step: "02", title: "Refer & Build Your Wings", body: "Share your unique left or right referral link. Place each new member on the side of your choice — set once, never changed." },
                            { icon: Wallet, step: "03", title: "Earn Nightly Pairing Bonuses", body: "Every night at midnight IST our engine calculates matched pairs. Earn ₹500 per pair, up to 10 pairs a day. Unused pairs carry forward automatically." },
                        ].map((s) => (
                            <div key={s.step} className="bg-white border border-slate-200 rounded-xl p-6 flex gap-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="shrink-0">
                                    <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#2563EB] grid place-items-center">
                                        <s.icon className="w-5 h-5" />
                                    </div>
                                </div>
                                <div>
                                    <div className="overline text-[#2563EB] mb-1">Step {s.step}</div>
                                    <h3 className="font-display font-bold text-lg text-[#1A3C5E]">{s.title}</h3>
                                    <p className="mt-1.5 text-slate-500 text-sm leading-relaxed">{s.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Earnings Plan ────────────────────────────────────────── */}
            <section id="earn" className="py-20 md:py-28 px-6 md:px-12 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="overline text-[#2563EB] mb-4">Compensation Plan</div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#1A3C5E]">Transparent. Immutable. Yours.</h2>
                        <p className="mt-4 text-slate-500 text-lg leading-relaxed">Two income streams, calculated daily, credited to your wallet — automatically.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Direct Bonus */}
                        <div className="bg-[#1A3C5E] text-white rounded-2xl p-8">
                            <div className="w-11 h-11 rounded-xl bg-white/10 grid place-items-center mb-6">
                                <Gift className="w-5 h-5 text-[#f59e0b]" />
                            </div>
                            <div className="overline text-blue-300 mb-2">Income Type 1</div>
                            <h3 className="font-display font-black text-2xl mb-2">Direct Bonus</h3>
                            <div className="font-display font-black text-5xl text-[#f59e0b] mb-1">₹800</div>
                            <div className="text-blue-200 text-sm mb-5">Per referral activation</div>
                            <ul className="space-y-2 text-sm text-blue-100">
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10b981]" /> ₹800 per activation</li>
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10b981]" /> Credited instantly on payment</li>
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10b981]" /> No limit on referrals</li>
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10b981]" /> Visible in wallet ledger immediately</li>
                            </ul>
                        </div>

                        {/* Pairing Bonus */}
                        <div className="bg-[#2563EB] text-white rounded-2xl p-8">
                            <div className="w-11 h-11 rounded-xl bg-white/20 grid place-items-center mb-6">
                                <Network className="w-5 h-5 text-[#f59e0b]" />
                            </div>
                            <div className="overline text-blue-200 mb-2">Income Type 2</div>
                            <h3 className="font-display font-black text-2xl mb-2">Pairing Bonus</h3>
                            <div className="font-display font-black text-5xl text-[#f59e0b] mb-1">₹500</div>
                            <div className="text-blue-200 text-sm mb-5">Per matched pair</div>
                            <ul className="space-y-2 text-sm text-blue-100">
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10b981]" /> ₹500 per pair</li>
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10b981]" /> Nightly — runs every midnight IST</li>
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10b981]" /> Up to 10 pairs/day (₹5,000 max)</li>
                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10b981]" /> Unused pairs carry forward forever</li>
                            </ul>
                        </div>

                        {/* Wing details */}
                        <div className="space-y-4">
                            <div className="bg-wing-l border border-emerald-200 rounded-xl p-6 h-[calc(50%-8px)]">
                                <div className="flex items-center gap-2 wing-l font-bold mb-3">
                                    <ArrowDownLeft className="w-5 h-5" /> Left Wing
                                </div>
                                <p className="text-slate-600 text-sm leading-relaxed">Place referrals on your left side to grow your left volume. Every new activation adds to your left active count.</p>
                                <div className="mt-3 font-display font-black text-2xl text-[#10b981]">Green</div>
                            </div>
                            <div className="bg-wing-r border border-orange-200 rounded-xl p-6">
                                <div className="flex items-center gap-2 wing-r font-bold mb-3">
                                    <ArrowDownRight className="w-5 h-5" /> Right Wing
                                </div>
                                <p className="text-slate-600 text-sm leading-relaxed">Build your right side to balance against the left. When left and right both have active members, they pair and you earn.</p>
                                <div className="mt-3 font-display font-black text-2xl text-[#ff7f50]">Coral</div>
                            </div>
                        </div>
                    </div>

                    {/* Daily max example */}
                    <div className="mt-8 bg-slate-900 text-white rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div>
                            <div className="overline text-slate-400 mb-2">Maximum Daily Example</div>
                            <div className="font-display font-black text-4xl md:text-5xl">10 pairs × ₹500 = ₹5,000 gross</div>
                            <div className="text-slate-400 mt-2">Net to you: <span className="text-white font-bold">₹3,750 / day</span> — that's ₹1,12,500 per month at peak</div>
                        </div>
                        <Link to="/register" className="shrink-0 inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#f59e0b] hover:bg-[#d97706] text-[#1A3C5E] font-bold text-sm transition-colors whitespace-nowrap">
                            Start Earning <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Membership Products ──────────────────────────────────── */}
            <section id="products" className="py-20 md:py-28 px-6 md:px-12 bg-[#1A3C5E]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#f59e0b]/20 text-[#f59e0b] text-xs font-bold uppercase tracking-widest mb-5">
                            <Package className="w-3.5 h-3.5" /> Membership Products
                        </div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white">
                            What You Receive When You Join
                        </h2>
                        <p className="mt-4 text-blue-200 text-lg leading-relaxed">
                            Every Zenwyxon membership includes one of these premium products — real value delivered to your door when you complete your registration purchase of ₹5,000.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {PRODUCTS.map((product) => (
                            <div key={product.name} className="bg-white rounded-2xl overflow-hidden shadow-xl flex flex-col">
                                {/* Image */}
                                <div className="overflow-hidden bg-slate-100">
                                    <ImgGallery images={product.images} alt={product.name} />
                                </div>

                                {/* Card body */}
                                <div className="p-6 flex flex-col flex-1">
                                    {/* Price badge */}
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div>
                                            <div className="font-display font-black text-xl text-[#1A3C5E] leading-tight">{product.name}</div>
                                            <div className="text-slate-500 text-sm mt-0.5">{product.subtitle}</div>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <div className="font-display font-black text-2xl text-[#2563EB]">{product.price}</div>
                                            <div className="text-xs text-slate-400">incl. membership</div>
                                        </div>
                                    </div>

                                    {/* Tagline */}
                                    <div className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${product.badge} mb-4 self-start`}>
                                        {product.tagline}
                                    </div>

                                    {/* Specs */}
                                    <ul className="space-y-2 mt-auto">
                                        {product.specs.map((spec, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                                <BadgeCheck className="w-4 h-4 text-[#10b981] shrink-0 mt-0.5" />
                                                {spec}
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA */}
                                    <Link to="/register" className="mt-6 flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-[#1A3C5E] hover:bg-[#2563EB] text-white font-semibold text-sm transition-colors">
                                        Join to Get This Product <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Bottom note */}
                    <div className="mt-10 text-center text-blue-300 text-sm">
                        <span className="font-semibold text-white">All 3 products are priced at ₹5,000</span> — your membership purchase includes product delivery + account activation + network earning eligibility.
                    </div>
                </div>
            </section>

            {/* ── Support & Training ───────────────────────────────────── */}
            <section className="py-20 md:py-28 px-6 md:px-12 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-xl mx-auto mb-14">
                        <div className="overline text-[#2563EB] mb-4">We've Got Your Back</div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#1A3C5E]">Support &amp; Training</h2>
                        <p className="mt-4 text-slate-500 leading-relaxed">Our team is with you from day one — helping you understand the plan, grow your network, and maximise your income.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Headphones, title: "Business Guidance", body: "One-on-one support to help you understand the binary plan and set realistic earning goals." },
                            { icon: Monitor, title: "Training Sessions", body: "Regular online and offline sessions on network building, product knowledge, and digital tools." },
                            { icon: TrendingUp, title: "Marketing Support", body: "Ready-made referral links, shareable content, and tips to help you grow your downline faster." },
                            { icon: Zap, title: "Digital Tools", body: "Full-featured dashboard, live tree visualiser, wallet ledger, and pairing history — all in one place." },
                        ].map((item, i) => (
                            <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm text-center hover:shadow-md hover:border-[#2563EB]/30 transition-all group">
                                <div className="w-14 h-14 rounded-2xl bg-blue-50 group-hover:bg-[#1A3C5E] text-[#2563EB] group-hover:text-white grid place-items-center mx-auto mb-5 transition-colors">
                                    <item.icon className="w-6 h-6" />
                                </div>
                                <h3 className="font-display font-bold text-lg text-[#1A3C5E] mb-2">{item.title}</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">{item.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA Banner ───────────────────────────────────────────── */}
            <section className="py-20 px-6 md:px-12 bg-gradient-to-r from-[#1A3C5E] to-[#2563EB]">
                <div className="max-w-4xl mx-auto text-center text-white">
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-tight">
                        Become part of a growing community<br className="hidden sm:block" /> where opportunity meets innovation
                    </h2>
                    <p className="mt-5 text-blue-100 text-lg max-w-2xl mx-auto leading-relaxed">
                        Join thousands of Zenwyxon members who are already building their networks and earning daily bonuses. Registration is free — your first purchase activates your account and starts the journey.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-4">
                        <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#f59e0b] hover:bg-[#d97706] text-[#1A3C5E] font-bold text-base transition-colors shadow-xl">
                            Register Now <ArrowRight className="w-5 h-5" />
                        </Link>
                        <a href="#contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold text-base transition-colors backdrop-blur">
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>

            {/* ── Contact ──────────────────────────────────────────────── */}
            <section id="contact" className="py-20 md:py-24 px-6 md:px-12 bg-white border-t border-slate-200">
                <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    <div>
                        <div className="overline text-[#2563EB] mb-4">Get in Touch</div>
                        <h3 className="font-display font-bold text-2xl text-[#1A3C5E] mb-4">Contact Us</h3>
                        <p className="text-slate-500 text-sm leading-relaxed">We are here to answer your questions and help you start your journey.</p>
                    </div>
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <Phone className="w-5 h-5 text-[#2563EB] mt-0.5 shrink-0" />
                            <div>
                                <div className="text-xs text-slate-400 mb-0.5 uppercase tracking-wider">Phone</div>
                                <a href="tel:+917386323388" className="text-slate-700 font-medium hover:text-[#2563EB] transition-colors">+91-73863 23388</a>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Mail className="w-5 h-5 text-[#2563EB] mt-0.5 shrink-0" />
                            <div>
                                <div className="text-xs text-slate-400 mb-0.5 uppercase tracking-wider">Email</div>
                                <a href="mailto:zenwyx.support@gmail.com" className="text-slate-700 font-medium hover:text-[#2563EB] transition-colors">zenwyx.support@gmail.com</a>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-[#2563EB] mt-0.5 shrink-0" />
                            <div>
                                <div className="text-xs text-slate-400 mb-0.5 uppercase tracking-wider">Location</div>
                                <span className="text-slate-700 font-medium">Hyderabad, Telangana, India</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Clock className="w-5 h-5 text-[#2563EB] mt-0.5 shrink-0" />
                            <div>
                                <div className="text-xs text-slate-400 mb-0.5 uppercase tracking-wider">Hours</div>
                                <span className="text-slate-700 font-medium">Mon–Sat, 8:00 AM – 10:00 PM</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="text-xs text-slate-400 uppercase tracking-wider mb-4">Follow Us</div>
                        <div className="flex gap-3 flex-wrap">
                            {["Facebook", "Twitter", "Instagram", "YouTube"].map(name => (
                                <a key={name} href="#" className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-[#1A3C5E] text-slate-600 hover:text-white text-xs font-semibold transition-colors">
                                    {name}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Footer ───────────────────────────────────────────────── */}
            <footer className="bg-[#1A3C5E] text-white py-12 px-6 md:px-12">
                <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
                    {/* Brand */}
                    <div>
                        <div className="mb-4"><ZenwyxonLogo size="md" /></div>
                        <p className="text-blue-200 text-sm leading-relaxed">India's trusted binary network marketing platform — transparent, immutable, and built for your growth.</p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <div className="text-xs text-blue-300 uppercase tracking-wider mb-4">Quick Links</div>
                        <ul className="space-y-2 text-sm text-blue-100">
                            {[
                                { label: "Home", href: "#" },
                                { label: "About Us", href: "#about" },
                                { label: "Why Choose Us", href: "#why" },
                                { label: "Earnings Plan", href: "#earn" },
                                { label: "Business Opportunity", href: "#opportunity" },
                                { label: "Contact", href: "#contact" },
                            ].map(l => (
                                <li key={l.label}><a href={l.href} className="hover:text-white transition-colors">{l.label}</a></li>
                            ))}
                        </ul>
                    </div>

                    {/* Member Links */}
                    <div>
                        <div className="text-xs text-blue-300 uppercase tracking-wider mb-4">Member Area</div>
                        <ul className="space-y-2 text-sm text-blue-100">
                            {[
                                { label: "Login", to: "/login" },
                                { label: "Register", to: "/register" },
                                { label: "Dashboard", to: "/dashboard" },
                                { label: "Shop Products", to: "/shop" },
                                { label: "My Wallet", to: "/wallet" },
                                { label: "My Tree", to: "/tree" },
                            ].map(l => (
                                <li key={l.label}><Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link></li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact snippet */}
                    <div>
                        <div className="text-xs text-blue-300 uppercase tracking-wider mb-4">Contact</div>
                        <ul className="space-y-3 text-sm text-blue-100">
                            <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 shrink-0" /> +91-73863 23388</li>
                            <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 shrink-0" /> zenwyx.support@gmail.com</li>
                            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> Hyderabad, Telangana</li>
                            <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 shrink-0" /> Mon–Sat 8 AM – 10 PM</li>
                        </ul>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-sm text-blue-300">
                    <div>© 2026 Zenwyxon. All rights reserved.</div>
                    <div className="flex gap-6">
                        <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
                        <a href="#" className="hover:text-white transition-colors">Terms of Use</a>
                        <a href="#" className="hover:text-white transition-colors">Legal</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
